import { DatePicker, Tooltip } from 'uikit';
import type { DatePickerPropOnChange, DatePickerProps, DatePickerPropType, TooltipProps, TooltipPropSize } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import { useCallback, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

type ControlledDateFieldProps = UseControllerProps<any> &
    DatePickerProps<DatePickerPropType> & {
        directionTooltip?: TooltipProps['direction'];
        classNameTooltip?: string;
        tooltipSize?: TooltipPropSize;
        isTooltipHidden?: boolean;
        onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
    };

export function ControlledDateField({
    name,
    control,
    rules,
    type = 'date',
    directionTooltip = 'rightCenter',
    classNameTooltip,
    tooltipSize = 's',
    isTooltipHidden,
    onTooltipAttributeChange,
    withClearButton = true,
    ...props
}: ControlledDateFieldProps) {
    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const handleOnChange: DatePickerPropOnChange<DatePickerPropType> = useCallback(
        ({ value }) => {
            field.onChange(value);
        },
        [field],
    );

    const ref = useRef(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <>
            <DatePicker
                type={type}
                value={field.value}
                onChange={handleOnChange}
                labelPosition="left"
                width="full"
                size="s"
                status={fieldState.error ? 'alert' : undefined}
                ref={ref}
                style={{ alignItems: 'center' }}
                withClearButton={withClearButton}
                {...props}
            />

            {!isTooltipHidden && fieldState.error?.message && (
                <Tooltip
                    size={tooltipSize}
                    direction={directionTooltip}
                    status="alert"
                    anchorRef={ref}
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {fieldState.error?.message}
                </Tooltip>
            )}
        </>
    );
}

ControlledDateField.displayName = 'ControlledDateField';
